import React, { useEffect, useRef } from 'react';
import { useTranslation } from '../../hooks/useTranslation';
import { useDiagramStore } from '../../stores/diagramStore';
import { cn } from '../../lib/utils';
import { GripVertical } from 'lucide-react';
import { Minimap } from './Minimap';

interface FramesPanelProps {
  onClose: () => void;
  onPanTo: (x: number, y: number) => void;
  readOnly?: boolean;
}

const displayDefaultFrameName = (
  raw: string,
  tg: (key: string, opts?: { n?: number; defaultValue?: string }) => string,
): string => {
  const familyMatch = /^Family (\d+)$/.exec(raw);
  if (familyMatch) {
    return tg('tools.defaultFamilyGroupName', { n: Number(familyMatch[1]), defaultValue: raw });
  }
  const genericMatch = /^Frame (\d+)$/.exec(raw);
  if (genericMatch) {
    return tg('tools.defaultGenericFrameName', { n: Number(genericMatch[1]), defaultValue: raw });
  }
  return raw;
};

export const FramesPanel: React.FC<FramesPanelProps> = ({ onClose, onPanTo, readOnly = false }) => {
  const { t } = useTranslation('diagram');
  const { t: tGenogram } = useTranslation('genogram');
  const framesFromStore = useDiagramStore(s => s.frames);
  const setFrames = useDiagramStore(s => s.setFrames);
  const [draggedIndex, setDraggedIndex] = React.useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = React.useState<number | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  // Click outside to close
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;
      if (target?.closest?.('[data-minimap-toggle="true"]')) {
        return;
      }
      if (panelRef.current && !panelRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    // Add delay to prevent immediate close on open
    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onClose]);

  // Sort frames by metadata.index if available, and assign index if missing
  const frames = React.useMemo(() => {
    const sorted = [...framesFromStore].sort((a, b) => {
      const aIndex = typeof a.metadata?.index === 'number' ? a.metadata.index : Infinity;
      const bIndex = typeof b.metadata?.index === 'number' ? b.metadata.index : Infinity;
      if (aIndex !== bIndex) return aIndex - bIndex;
      return a.name.localeCompare(b.name);
    });

    if (!readOnly) {
      let needsUpdate = false;
      sorted.forEach((frame, index) => {
        if (typeof frame.metadata?.index !== 'number') {
          frame.metadata = { ...frame.metadata, index };
          needsUpdate = true;
        }
      });

      if (needsUpdate) {
        setFrames(sorted);
      }
    }

    return sorted;
  }, [framesFromStore, readOnly, setFrames]);

  const handleFrameClick = (frame: typeof frames[0]) => {
    const frameX = frame.bounds.x + frame.bounds.width / 2;
    const frameY = frame.bounds.y + frame.bounds.height / 2;
    onPanTo(frameX, frameY);
  };

  const handleDragStart = (e: React.DragEvent, index: number) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverIndex(index);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDraggedIndex(null);
      setDragOverIndex(null);
      return;
    }

    const newFrames = [...frames];
    const [draggedFrame] = newFrames.splice(draggedIndex, 1);
    newFrames.splice(dropIndex, 0, draggedFrame);

    // Update metadata.index for all frames (mutate in place to maintain references)
    newFrames.forEach((frame, index) => {
      frame.metadata = { ...frame.metadata, index };
    });

    setFrames(newFrames);
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  return (
    <div
      ref={panelRef}
      className={cn(
        "absolute bottom-20 right-6 z-50",
        "bg-background border rounded-lg shadow-xl",
        "animate-in slide-in-from-bottom-5 duration-200",
        "w-[280px]"
      )}
    >
      <div className="flex items-center px-3 py-2 border-b">
        <h3 className="text-sm font-semibold text-foreground">{t('framesPanel.title')}</h3>
      </div>

      <div className="flex flex-col">
        {frames.length > 0 && (
          <div className="px-3 py-3">
            <h4 className="text-xs font-semibold tracking-wide text-muted-foreground uppercase mb-2">
              {t('tools.frames')}
            </h4>
            <div className="space-y-1 max-h-[200px] overflow-y-auto">
              {frames.map((frame, index) => (
                <div
                  key={frame.id}
                  draggable={!readOnly}
                  onDragStart={readOnly ? undefined : (e) => handleDragStart(e, index)}
                  onDragOver={readOnly ? undefined : (e) => handleDragOver(e, index)}
                  onDragEnd={readOnly ? undefined : handleDragEnd}
                  onDrop={readOnly ? undefined : (e) => handleDrop(e, index)}
                  className={cn(
                    "w-full flex items-center gap-2 px-2 py-1.5",
                    "rounded transition-colors group",
                    readOnly ? "cursor-default" : "cursor-move",
                    draggedIndex === index && "opacity-40",
                    dragOverIndex === index && draggedIndex !== index && "bg-muted"
                  )}
                >
                  {!readOnly && (
                    <GripVertical className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0 cursor-grab active:cursor-grabbing" />
                  )}
                  <button
                    onClick={() => handleFrameClick(frame)}
                    className="flex-1 text-left"
                  >
                    <span className="text-sm text-foreground hover:text-primary transition-colors">
                      {index + 1}.{' '}
                      {frame.name ? displayDefaultFrameName(frame.name, tGenogram) : t('framesPanel.untitledFrame')}
                    </span>
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="p-3">
          <Minimap onClose={() => {}} onPanTo={onPanTo} hideHeader={true} />
        </div>
      </div>
    </div>
  );
};
